import { List } from 'immutable';
import { RoomStaySummary } from '../models/room-stay-summary';
import { RoomTypeRateSummary, RoomTypeRateSummarySort } from '../models/room-type-rate-summary';
import { StaySummary } from '../models/stay-summary';
import { Transaction } from '../models/transaction';
import { TranslateService } from '@ngx-translate/core';
import { Reservation } from '../models/reservation';
import Decimal from 'decimal.js';
import { MathUtils } from './math-utils';

export class TransactionUtils {

  /**
   * Transforms a list of transactions (Reservation or RSVP) into a RoomStaySummary object
   * Contains a stay summary and a room summary
   *
   * Immutable JS doc on groupBy
   * https://facebook.github.io/immutable-js/docs/#/List/groupBy
   *
   * @param transactions {List}
   * @param translateService {TranslateService}
   * @returns RoomStaySummary
   */
  static transformTransactionsToSummary<T extends Transaction>(transactions: List<T>,
                                                               translateService: TranslateService): RoomStaySummary {
    let roomTypeRateSummaryList = List<RoomTypeRateSummary>([]);

    // create lists of Reservations grouped by type and rate
    let listOfTransactionsGroupedByTypeAndRate = List<List<T>>([]);

    // this has to be re-created as a list or else groupBy is not available
    List(transactions).groupBy(roomTypeGroup => roomTypeGroup.roomType)
      .forEach(roomTypeGroup => {
        roomTypeGroup
          .groupBy(roomRateGroup => roomRateGroup.roomRate)
          .forEach(roomRateGroup => {
            listOfTransactionsGroupedByTypeAndRate = listOfTransactionsGroupedByTypeAndRate.push(roomRateGroup.toList());
          });
      });

    // iterate through each list to calculate aggregations (grouped by type and rate)
    let totalNights = 0;
    let totalRevenue = new Decimal(0);
    let totalCommission = new Decimal(0);
    let totalCommissionableRevenue = new Decimal(0);
    let commissionPercentage: number = undefined;

    listOfTransactionsGroupedByTypeAndRate.forEach((roomTypeAndRateGroup: List<T>) => {
      const roomTypeRateSummary: RoomTypeRateSummary = TransactionUtils.transformTransactionListToRoomSummary(
        roomTypeAndRateGroup, translateService);

      // sum total nights, total revenue and totalCommission
      totalNights = totalNights + roomTypeRateSummary.totalNights;
      totalRevenue = totalRevenue.add(roomTypeRateSummary.totalRevenue);
      totalCommission = totalCommission.add(roomTypeRateSummary.totalCommission);

      // adding room type and rate summary to list
      roomTypeRateSummaryList = roomTypeRateSummaryList.push(roomTypeRateSummary);

      totalCommissionableRevenue = totalCommissionableRevenue.add(roomTypeRateSummary.totalCommissionableRevenue);
      commissionPercentage = roomTypeRateSummary.commissionPercentage;
    });

    totalCommission = MathUtils.roundDecimalCurrencyToCents(totalCommission);

    totalRevenue =  MathUtils.roundDecimalCurrencyToCents(totalRevenue);

    // Calculate avgRoomRate: totalRevenue / totalNights
    let avgRoomRate = new Decimal(0);
    if (totalNights > 0) {
      avgRoomRate = totalRevenue.div(totalNights);
      avgRoomRate = MathUtils.roundDecimalCurrencyToCents(avgRoomRate);
    }

    // Generate a room summary
    const staySummary = new StaySummary({
      totalNights: totalNights,
      totalRevenue: totalRevenue.toNumber(),
      avgRoomRate: avgRoomRate.toNumber(),
      totalItems: transactions.size,
      totalCommission: totalCommission.toNumber()
    });

    // build the room stay Summary
    return new RoomStaySummary({
      roomTypeRateSummaryList: roomTypeRateSummaryList.sort(RoomTypeRateSummarySort).toList(),
      staySummary: staySummary
    });
  }

  /**
   * Transforms a list of transactions into a RoomTypeRateSummary
   *
   * @param roomList {List}
   * @param translateService {TranslateService}
   * @returns RoomTypeRateSummary
   */
  static transformTransactionListToRoomSummary<T extends Transaction>(
    roomList: List<T>,
    translateService: TranslateService): RoomTypeRateSummary {

    let roomType = '';
    let roomRate = new Decimal(0);
    let commissionPercentage: number = undefined;
    let totalNights = 0;
    let totalRevenue = new Decimal(0);
    let totalCommission = new Decimal(0);
    let totalCommissionableRevenue = new Decimal(0); // totalCommissionableRevenue is the sum of all room revenues that are paid via GP

    roomList.forEach((room: T) => {
      roomType = room.roomType ? room.roomType : translateService.instant('EVENT.OVERVIEW_CARDS.UNKNOWN_ROOM_TYPE');
      roomRate = new Decimal(room.roomRate);
      commissionPercentage = room.commissionPercentage;

      const roomNights = room.nights * this.getRooms(room);
      totalNights = totalNights + roomNights;

      const numberOfRooms = Math.max(1, this.getRooms(room));
      const roomRevenue = roomRate.times(room.nights).times(numberOfRooms);
      totalRevenue = totalRevenue.add(roomRevenue);

      const roomCommission = room.payViaGp ? room.commission : 0;
      totalCommission = totalCommission.add(roomCommission || 0);

      if (room.payViaGp) {
        totalCommissionableRevenue = totalCommissionableRevenue.add(roomRevenue);
      }
    });

    return new RoomTypeRateSummary({
      roomType: roomType,
      roomRate: roomRate.toNumber(),
      totalNights: totalNights, // Note: this is called totalNights but it is actually "total room nights"
      totalRevenue: totalRevenue.toNumber(),
      totalCommission: totalCommission.toNumber(),
      totalCommissionableRevenue: totalCommissionableRevenue,
      commissionPercentage: commissionPercentage,
    });
  }

  /**
   * Returns the number of rooms
   * @param room
   */
  static getRooms(room: Transaction) {
    let rooms = 1;
    if (this.isReservation(room)) {
      rooms = (room as Reservation).rooms;
    }

    return rooms;
  }

  /**
   * Returns `true` if the room is of type Reservation and has the `rooms` property; `false` otherwise
   * @param {any} room
   */
  static isReservation(room: Transaction): room is Reservation {
    return (room as Reservation).rooms !== undefined;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/utils/transaction-utils.ts